import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Alert,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../contexts/SnackbarContext';
import TranscriptionCard from './TranscriptionCard';
import { TranscriptionService, Transcription } from '../../services/TranscriptionService';

const TranscriptionList: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchTranscriptions = async () => {
      try {
        setLoading(true);
        const data = await TranscriptionService.list();
        console.log('Transcrições recebidas:', data);
        setTranscriptions(data);
      } catch (error) {
        console.error('Erro ao buscar transcrições:', error);
        setError('Erro ao carregar as transcrições');
      } finally {
        setLoading(false);
      }
    };

    fetchTranscriptions();
  }, []);

  const loadTranscriptions = async () => {
    try {
      setLoading(true);
      const data = await TranscriptionService.list();
      setTranscriptions(data);
    } catch (error) {
      console.error('Erro ao carregar transcrições:', error);
      showSnackbar('Erro ao carregar transcrições', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await TranscriptionService.delete(id);
      setTranscriptions(prev => prev.filter(t => t.id !== id));
      showSnackbar('Transcrição excluída com sucesso', 'success');
    } catch (error) {
      console.error('Erro ao excluir transcrição:', error);
      showSnackbar('Erro ao excluir transcrição', 'error');
    }
  };

  const handleEdit = async (id: number, field: string, value: string) => {
    try {
      const updatedTranscription = await TranscriptionService.update(id, field, value);
      setTranscriptions(prev =>
        prev.map(t => t.id === id ? updatedTranscription : t)
      );
      showSnackbar('Transcrição atualizada com sucesso', 'success');
    } catch (err) {
      console.error('Erro ao atualizar transcrição:', err);
      showSnackbar('Erro ao atualizar transcrição', 'error');
    }
  };

  const handleFormat = async (id: number) => {
    try {
      const updatedTranscription = await TranscriptionService.format(id);
      setTranscriptions(prev =>
        prev.map(t => t.id === id ? updatedTranscription : t)
      );
      showSnackbar('Transcrição formatada com sucesso', 'success');
    } catch (err) {
      console.error('Erro ao formatar transcrição:', err);
      showSnackbar('Erro ao formatar transcrição', 'error');
    }
  };

  const handlePlay = (id: number) => {
    const transcription = transcriptions.find(t => t.id === id);
    if (transcription?.audioFilePath) {
      // Implementar lógica de reprodução
      console.log('Reproduzindo áudio:', transcription.audioFilePath);
    }
  };

  const handleCopy = (id: number) => {
    const transcription = transcriptions.find(t => t.id === id);
    if (transcription?.transcriptionText) {
      navigator.clipboard.writeText(transcription.transcriptionText)
        .then(() => showSnackbar('Texto copiado com sucesso!', 'success'))
        .catch(() => showSnackbar('Erro ao copiar texto', 'error'));
    }
  };

  const filteredTranscriptions = transcriptions
    .filter(t => {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (t.patientName || '').toLowerCase().includes(searchTermLower) ||
        (t.transcriptionText || '').toLowerCase().includes(searchTermLower) ||
        (t.notes || '').toLowerCase().includes(searchTermLower);
      
      const matchesStatus = statusFilter === 'all' || 
        (t.status || '').toLowerCase() === statusFilter.toLowerCase();
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Minhas Transcrições
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/doctor/transcription/new')}
        >
          Nova Transcrição
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Buscar transcrições"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="pendente">Pendente</MenuItem>
              <MenuItem value="em andamento">Em Andamento</MenuItem>
              <MenuItem value="concluído">Concluído</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {filteredTranscriptions.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            Nenhuma transcrição encontrada
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredTranscriptions.map((transcription) => (
            <Grid item xs={12} key={transcription.id}>
              <TranscriptionCard
                transcription={transcription}
                onDelete={handleDelete}
                onEdit={(id, field, value) => handleEdit(id, field, value)}
                onFormat={handleFormat}
                onPlay={handlePlay}
                onCopy={handleCopy}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default TranscriptionList;
